import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { User } from '@auth/user';
import { authUpdateDbUser } from '@auth/authApi';
import _ from 'lodash';
import setIn from '@/utils/setIn';
import { logoutHandler } from '@/utils/logoutHandler';
import { shouldMakeApiCalls } from '@/utils/pageUtils';
import { AUTH_USER_UPDATED_EVENT, getAccessToken, getUser, setUser, clearAuthStorage } from '@/utils/localStorage';

type UseUserReturn = {
	data: User | null;
	isGuest: boolean;
	updateUser: (updates: Partial<User>) => Promise<User | undefined>;
	updateUserSettings: (newSettings: User['settings']) => Promise<User['settings'] | undefined>;
	signOut: () => Promise<void>;
	refreshUser: () => Promise<void>;
};

function useUser(): UseUserReturn {
	const location = useLocation();
	const pathname = location.pathname;
	const [user, setUserState] = useState<User | null>(null);
	const [isGuest, setIsGuest] = useState<boolean>(true);
	const [lastAccessToken, setLastAccessToken] = useState<string | null>(null);
	const isFetchingRef = useRef(false);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const isInitialLoadRef = useRef(true);

	// Check if we should make API calls on this page
	const shouldFetch = shouldMakeApiCalls(pathname || '');

	const fetchUserData = useCallback(async () => {
		// Prevent API calls on auth pages, during fetching, or after logout
		if (!shouldFetch || isFetchingRef.current || logoutHandler.isUserLoggedOut()) {
			return;
		}

		isFetchingRef.current = true;

		try {
			// Get token and user from localStorage instead of cookies
			const currentAccessToken = getAccessToken();
			const storedUser = getUser();

			// Always fetch user data on initial load or when token changes
			if (currentAccessToken && storedUser && (isInitialLoadRef.current || currentAccessToken !== lastAccessToken)) {
				setUserState(storedUser);
				setIsGuest(false);
				setLastAccessToken(currentAccessToken);
				isInitialLoadRef.current = false;
				logoutHandler.setLoggedOut(false);
			} else if (!currentAccessToken || !storedUser) {
				// User logged out - stop all polling
				if (!isGuest) {
					setUserState(null);
					setIsGuest(true);
					setLastAccessToken(null);
					logoutHandler.setLoggedOut(true);
					
					// Clear interval immediately on logout
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
						intervalRef.current = null;
					}
				}
			}
		} catch (error) {
			console.error('Error fetching user data:', error);
			
			// Only reset user state if we're sure there's an error
			if (!isGuest) {
				setUserState(null);
				setIsGuest(true);
				setLastAccessToken(null);
				logoutHandler.setLoggedOut(true);
				
				// Clear interval on error
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
			}
		} finally {
			isFetchingRef.current = false;
		}
	}, [shouldFetch, lastAccessToken, isGuest]);

	// Initial fetch only - but not on auth pages
	useEffect(() => {
		if (shouldFetch && !logoutHandler.isUserLoggedOut()) {
			fetchUserData();
		}
	}, [shouldFetch, fetchUserData]);

	// Set up periodic check with longer interval and cleanup
	useEffect(() => {
		// Don't set up polling if user is logged out or on auth pages
		if (!shouldFetch || logoutHandler.isUserLoggedOut()) {
			// Clear any existing interval when on auth pages
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		// Clear any existing interval
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		// Only set up polling if we have a user or are still checking initially
		if (user || isInitialLoadRef.current) {
			const pollingDelay = user ? 60000 : 10000; // 1 minute if user exists, 10 seconds if checking initially
			intervalRef.current = setInterval(() => {
				if (shouldFetch && !logoutHandler.isUserLoggedOut()) {
					fetchUserData();
				}
			}, pollingDelay);
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [user, fetchUserData, shouldFetch]);

	// Listen for storage events (when user logs out in another tab)
	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if ((e.key === 'access_token' || e.key === 'user') && shouldFetch && !logoutHandler.isUserLoggedOut()) {
				// Debounce storage events to prevent multiple calls
				setTimeout(() => {
					if (shouldFetch && !logoutHandler.isUserLoggedOut()) {
						fetchUserData();
					}
				}, 100);
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, [fetchUserData, shouldFetch]);

	// Listen for same-tab user updates (e.g. profile edits)
	useEffect(() => {
		const handleUserUpdated = (event: Event) => {
			const customEvent = event as CustomEvent<User | null>;
			const updatedUser = customEvent.detail;

			if (updatedUser) {
				setUserState(updatedUser);
				setIsGuest(false);
			}
		};

		window.addEventListener(AUTH_USER_UPDATED_EVENT, handleUserUpdated);

		return () => {
			window.removeEventListener(AUTH_USER_UPDATED_EVENT, handleUserUpdated);
		};
	}, []);

	// Listen for focus events to refresh user data when user returns to tab
	useEffect(() => {
		const handleFocus = () => {
			// Only refresh if should fetch, not logged out and not currently fetching
			if (!isFetchingRef.current && shouldFetch && !logoutHandler.isUserLoggedOut()) {
				fetchUserData();
			}
		};

		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, [fetchUserData, shouldFetch]);

	// Listen for global logout events
	useEffect(() => {
		const handleLogout = () => {
			// Clear all intervals and state when logout is triggered globally
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			setUserState(null);
			setIsGuest(true);
			setLastAccessToken(null);
		};

		logoutHandler.addLogoutListener(handleLogout);
		
		return () => {
			logoutHandler.removeLogoutListener(handleLogout);
		};
	}, []);

	async function handleUpdateUser(updates: Partial<User>) {
		const response = await authUpdateDbUser(updates);

		if (!response.ok) {
			throw new Error('Failed to update user');
		}

		const updatedUser = (await response.json()) as User;

		setUserState(updatedUser);
		// Update localStorage with new user data
		setUser(updatedUser);

		return updatedUser;
	}

	/**
	 * Update user settings
	 */
	async function handleUpdateUserSettings(newSettings: User['settings']) {
		if (!user) return undefined;

		const newUser = setIn(user, 'settings', newSettings) as User;

		if (_.isEqual(user, newUser)) {
			return undefined;
		}

		const updatedUser = await handleUpdateUser(newUser);

		return updatedUser?.settings;
	}

	/**
	 * Sign out - clears localStorage and redirects
	 */
	async function handleSignOut() {
		// Set logout flag immediately to stop all polling globally
		logoutHandler.setLoggedOut(true);
		
		// Clear local state immediately
		setUserState(null);
		setIsGuest(true);
		setLastAccessToken(null);
		isInitialLoadRef.current = true;
		
		// Clear interval immediately
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		// Clear localStorage
		clearAuthStorage();
	}

	/**
	 * Refresh user data manually
	 */
	async function refreshUser() {
		if (shouldFetch && !logoutHandler.isUserLoggedOut()) {
			await fetchUserData();
		}
	}

	return {
		data: user,
		isGuest,
		signOut: handleSignOut,
		updateUser: handleUpdateUser,
		updateUserSettings: handleUpdateUserSettings,
		refreshUser
	};
}

export default useUser;

